import { useApplication } from "@pixi/react";
import { useEffect, useState, type ReactNode } from "react";
import { Game } from "@engine/core/Game";
import { SceneManager } from "./scenes/SceneManager";
import { EngineContext, type EngineContextValue } from "./engine-context";

interface EngineHostProps {
  children: ReactNode;
  onReady?: (ctx: EngineContextValue) => void | Promise<void>;
}

/**
 * Initializes the Game once Pixi's Application is ready, exposes it via context,
 * and tears it down on unmount.
 */
export function EngineHost({ children, onReady }: EngineHostProps): ReactNode {
  const { app, isInitialised } = useApplication() as {
    app: import("pixi.js").Application | undefined;
    isInitialised: boolean;
  };
  const [ctx, setCtx] = useState<EngineContextValue | null>(null);

  useEffect(() => {
    if (!app || !isInitialised || !app.canvas) return;
    const game = new Game(app);
    const scenes = new SceneManager(game);
    game.start();
    const value: EngineContextValue = { game, scenes };
    setCtx(value);
    void onReady?.(value);
    return () => {
      game.destroy();
      setCtx(null);
    };
  }, [app, isInitialised, onReady]);

  if (!ctx) return null;
  return <EngineContext.Provider value={ctx}>{children}</EngineContext.Provider>;
}
